import { ChangeEvent, useMemo, useState } from 'react';
import { CustomerAssignableProduct } from 'helpers/hooks/useCustomerAssignableProducts';
import { useCustomerAssignableProducts } from '@hooks';
import { SelectFetch } from '@molecules';

interface SelectCustomerProductProps {
  customerProducts?: CustomerAssignableProduct[];
  onChange?: (customerProductsSelected: CustomerAssignableProduct[]) => void;
  customerId: number;
}

/**
 * SelectProduct component renders a form to select and add products.
 * It includes a dropdown to fetch and select products and a button to submit the selected products.
 *
 * @component
 * @param props - The component props
 * @param  props.customerProducts - The current selected value(s) of the SelectFetch component
 * @param  props.handleChange - Function to handle the change event
 * @param  props.customerId - customerId to get assignable products
 * @returns The rendered component
 */
function SelectCustomerProduct({
  customerProducts,
  onChange,
  customerId,
}: SelectCustomerProductProps) {
  const { data, isLoading } = useCustomerAssignableProducts({ customerId });
  const [customerProductsOption, setCustomerProductsOption] = useState<string[]>([]);
  const value = useMemo(
    () => (customerProducts ? customerProducts.map((cp) => cp.id) : customerProductsOption),
    [customerProducts, customerProductsOption]
  );

  /** Handle changes in select customer product  */
  const handleChange = (event: ChangeEvent & { target: { value: string[] } }) => {
    const customerProductsSelected: CustomerAssignableProduct[] = [];

    event.target.value.forEach((customerProductId: string) => {
      const customerProduct = data?.find(
        (customerProduct) => customerProduct.id === customerProductId
      );

      if (customerProduct) {
        customerProductsSelected.push(customerProduct);
      }
    });

    setCustomerProductsOption(event.target.value);
    onChange && onChange(customerProductsSelected);
  };

  return (
    <SelectFetch
      multiple
      placeholder='EMPLOYEES:PRODUCT_LABEL'
      value={value}
      parseOption={(product) => ({
        value: product.id,
        text: product.displayName,
        provider: product.provider,
      })}
      getOptions={() => {
        return { data, isLoading };
      }}
      onChange={handleChange}
    />
  );
}

export default SelectCustomerProduct;
